import {
  Button,
  Flex,
  Modal,
  Divider,
  Space,
  Text,
  LoadingOverlay,
  Alert,
} from "@mantine/core";
import { DatePicker } from "@mantine/dates";
import dayjs from "dayjs";
import Link from "next/link";
import type { FC } from "react";
import { memo, useState } from "react";
import { AvatarIconAndNameLinkMemo } from "src/component/element";
import { ConfirmModalMemo } from "src/component/element/ConfirmModal/ConfirmModal";
import { TimePicker } from "src/component/pageComponent/Fumufumu/CreateForm/TimePicker";
import {
  errorShowNotification,
  successShowNotification,
} from "src/lib/mantine";
import { useGetUserQuery, useWithdrawalMutation } from "src/service/UsersApi";

import styles from "./Withdrawal.module.scss";

const WithdrawalConfirmModal: FC<{
  opened: boolean;
  userId: string;
  isAdmin?: boolean;
  onClose: () => void;
}> = ({ onClose, isAdmin, userId, opened }) => {
  const [isConfirm, setIsConfirm] = useState(false);
  const [expectedDelDate, setExpectedDelDate] = useState<{
    date: Date | null;
    time: string;
  }>({ date: null, time: "" });
  const { data } = useGetUserQuery(userId);
  const [withdrawal, { isLoading }] = useWithdrawalMutation();

  const confirm = async () => {
    setIsConfirm(false);
    const times = expectedDelDate.time.length
      ? expectedDelDate.time.split(":")
      : [0, 0];
    const scheduledDeletionAt =
      expectedDelDate.date &&
      new Date(
        expectedDelDate.date.getFullYear(),
        expectedDelDate.date.getMonth(),
        expectedDelDate.date.getDate(),
        +times[0],
        +times[1],
        0,
        0,
      );
    await withdrawal({
      userId,
      scheduledDeletionAt: scheduledDeletionAt
        ? scheduledDeletionAt?.toISOString()
        : undefined,
    })
      .unwrap()
      .then(() => {
        successShowNotification("成功した");
        onClose();
      })
      .catch(() => {
        errorShowNotification("失敗しました");
      });
  };

  return (
    <>
      {!isConfirm && (
        <Modal
          opened={opened}
          onClose={onClose}
          withCloseButton={false}
          padding={5}
          radius={12}
        >
          <Flex justify="space-between" align="center" p={5}>
            {data?.id &&
              (isAdmin ? (
                <AvatarIconAndNameLinkMemo
                  userId={data?.id}
                  profileImage={data?.profileImage}
                  nickname={data?.nickname}
                />
              ) : (
                <h4>アカウント削除について</h4>
              ))}
          </Flex>
          <Divider />

          {isAdmin && (
            <Flex p={10} direction="column">
              <Flex align={"end"} gap={20}>
                <DatePicker
                  label="退会予定日"
                  value={expectedDelDate.date}
                  firstDayOfWeek="sunday"
                  locale="ja"
                  inputFormat="YYYY/MM/DD"
                  labelFormat="YYYY/MM"
                  // minDate={dayjs().toDate()}
                  onChange={(value) => {
                    setExpectedDelDate({
                      ...expectedDelDate,
                      date: value,
                      time: value ? expectedDelDate.time : ":",
                    });
                  }}
                />
                <TimePicker
                  label="時間"
                  value={expectedDelDate.time}
                  onSelect={(_, value) => {
                    if (value) {
                      setExpectedDelDate({
                        ...expectedDelDate,
                        time: dayjs(value).format("HH:mm"),
                      });
                    }
                  }}
                />
              </Flex>
              <Text mt={5} size={11} className={styles.des}>
                日付を選択しない場合、デフォルトで今日の日付が設定されます。
              </Text>
              <Alert variant="light" color="orange" mt={15}>
                アカウントが無効化される前は、通常通りログインして利用できます。無効化日を過ぎると、以下のようにアカウントが削除されます。
                <ul className={styles.msg}>
                  <li>
                    アカウントが削除された後、このアカウントでは再度ログインすることはできません。
                  </li>
                  <li>
                    関する情報、投稿記事、受講中のコース、クラスなどもすべて削除されます。
                  </li>
                </ul>
                アカウント削除を決定する前に、情報を十分にご確認ください。
              </Alert>
            </Flex>
          )}
          {!isAdmin && (
            <Text mt={16} p={5} pl={10} pr={10} sx={{ lineHeight: "200%" }}>
              <strong> 退会後30日が経過すると：</strong>
              <ul className={styles.msg}>
                <li>
                  あなたのアカウントは完全に削除され、再度ログインすることはできなくなります。
                </li>
                <li>
                  あなたに関する情報、投稿記事、受講中のコース、クラスなどもすべて削除されます。
                </li>
                <li>
                  削除予定日までの30日間は、アカウントを再び有効化することが可能です。
                </li>
              </ul>
              アカウント削除を決定する前に、情報を十分にご確認ください。
            </Text>
          )}

          {!isAdmin && (
            <Flex justify="center" align="center" gap={10} mt={15}>
              <Link
                href={"https://tanqbase.com/terms_of_use/"}
                target="_blank"
                className={styles.privacyText}
              >
                利用規約
              </Link>
              <Link
                href={"https://replayce.co.jp/privacy-policy"}
                target="_blank"
                className={styles.privacyText}
              >
                プライバシーポリシー
              </Link>
            </Flex>
          )}

          <Space h={20} />
          <Divider />
          <Space h={10} />
          <Flex align="center" w="100%" gap={8} justify="center">
            <Button className={styles.modalBackButton} onClick={onClose}>
              戻る
            </Button>
            <Button
              type="submit"
              className={styles.modalSubmitButton}
              onClick={() => setIsConfirm(true)}
            >
              決定
            </Button>
          </Flex>
        </Modal>
      )}
      {!isLoading && (
        <ConfirmModalMemo
          opened={isConfirm}
          message="アカウントを削除してもよろしいですか?"
          onClose={() => setIsConfirm(false)}
          onConfirm={confirm}
          title={"アカウントの削除"}
        />
      )}

      {isLoading && <LoadingOverlay visible />}
    </>
  );
};

export const WithdrawalConfirmModalMemo = memo(WithdrawalConfirmModal);
